import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

/**
 * DetailSection - A themed section component for detail pages
 *
 * Use this component to create consistent sections on detail pages
 * with a title and content area. Supports theme-aware styling.
 *
 * @example
 * // Basic usage
 * <DetailSection title="User Information">
 *   <DetailSection.Grid>
 *     <DetailSection.Item label="Name" value="John Doe" />
 *     <DetailSection.Item label="Email" value="john@example.com" />
 *   </DetailSection.Grid>
 * </DetailSection>
 *
 * @example
 * // With custom content
 * <DetailSection title="Actions">
 *   <ButtonV2>Edit Profile</ButtonV2>
 * </DetailSection>
 */

const SectionContainer = styled.div`
  margin-bottom: ${(props) => (props.noMargin ? '0' : '24px')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.08)'};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 2}, 1fr);
  gap: ${(props) => props.gap || '16px'};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

// Grid subcomponent for layout
const Grid = ({ children, columns = 2, gap = '16px' }) => {
  return (
    <GridContainer columns={columns} gap={gap}>
      {children}
    </GridContainer>
  );
};

Grid.propTypes = {
  children: PropTypes.node.isRequired,
  /** Number of columns (default: 2) */
  columns: PropTypes.number,
  /** Gap between items (default: 16px) */
  gap: PropTypes.string,
};

// Item subcomponent for label/value pairs
const Item = ({ label, value, children }) => {
  return (
    <ItemContainer>
      <ItemLabel>{label}</ItemLabel>
      {children ? children : <ItemValue>{value ?? '-'}</ItemValue>}
    </ItemContainer>
  );
};

Item.propTypes = {
  /** Label text */
  label: PropTypes.string.isRequired,
  /** Value to display */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
  /** Custom content to render instead of value */
  children: PropTypes.node,
};

// Main DetailSection component
const DetailSection = ({ title, children, noMargin = false, className }) => {
  return (
    <SectionContainer noMargin={noMargin} className={className}>
      {title && <SectionTitle>{title}</SectionTitle>}
      {children}
    </SectionContainer>
  );
};

DetailSection.propTypes = {
  /** Section title */
  title: PropTypes.string,
  /** Section content */
  children: PropTypes.node.isRequired,
  /** Remove bottom margin */
  noMargin: PropTypes.bool,
  /** Additional className */
  className: PropTypes.string,
};

// Attach subcomponents as static properties
DetailSection.Grid = Grid;
DetailSection.Item = Item;

export default DetailSection;
